import { isObject } from 'lodash';
import { useMemo, useRef } from 'react';
import type { IdValueType } from '../@types/common';
import type {
  CauseDiagramNode,
  ConsequenceDiagramNode,
  ControlDiagramNode,
  DiagramConfiguration,
  EscalationFactorBarrierDiagramNode,
  EscalationFactorDiagramNode,
} from '../@types/diagram';
import type { TransformedSimpleRecordDto } from '../api/@types/enhanced-v4-api.types';
import { useGetSimpleRecordQuery, useGetSimpleRecordsQuery } from '../api/enhanced/enhanced-v4-api';
import { useConfigurationContext } from '../context/configuration.context';
import { useDiagramContext } from '../context/diagram.context';
import { criticalFilterValues, effectiveFilterValues } from '../helpers/constants';
import { CriticalControlEnum, EffectiveControlEnum } from '../helpers/node-util';

/**
 * Hook for managing and constructing diagram data based on record information.
 *
 * This hook handles the fetching and processing of:
 * - Main scenario/MUE record
 * - Associated causes and their preventative controls
 * - Associated consequences and their mitigating controls
 *
 * The hook constructs a diagram configuration containing nodes and relationships
 * between different elements (causes, consequences, controls, etc.).
 *
 * @returns {Object} An object containing:
 *   - isFetching: boolean - Indicates if any data is currently being fetched
 *   - diagramData: DiagramConfiguration | undefined - The constructed diagram data structure
 *
 * @remarks
 * The hook relies on URL parameters 'records' or 'recordId' to identify the main record.
 * If no record ID is provided, it initializes an empty diagram in edit mode.
 *
 * @example
 * ```typescript
 * const { isFetching, diagramData } = useRecord();
 * ```
 */
const useRecord = () => {
  const diagramDataRef = useRef<DiagramConfiguration | undefined>(undefined);
  const { recordId } = useDiagramContext();

  const isRecordIdValid = useMemo(() => {
    if (recordId) {
      const recordIdNumber = Number(recordId);
      return Boolean(isFinite(recordIdNumber) && recordIdNumber > 0);
    }

    // no recordId is considered valid as we are probably working with a new diagram.
    return true;
  }, [recordId]);

  const configuration = useConfigurationContext();

  const {
    main: {
      scenarioFieldId,
      hazardFieldId,
      causesReverseRecordLinkFieldId,
      consequencesReverseRecordLinkFieldId,
      mitigatingControlsRecordLinkFieldId,
      preventativeControlsRecordLinkFieldId,
      escalationFactorsReverseRecordLinkFieldId,
    },
    causes: {
      formId: causesFormId,
      fieldId: causesCaptionFieldId,
      controlsReverseRecordLinkFieldId: causesControlsRecordLinkFieldId,
    },
    consequences: {
      formId: consequencesFormId,
      fieldId: consequencesCaptionFieldId,
      controlsReverseRecordLinkFieldId: consequencesControlsRecordLinkFieldId,
    },
    controls: {
      form: { workflowSteps: controlFormWorkflowSteps },
      formId: controlFormId,
      fieldId: controlCaptionFieldId,
      controlTypeFieldId,
      controlEffectivenessFieldId,
      criticalOrNonCriticalFieldId,
      effectiveOrNotEffectiveField,
      isGlobalFieldId: globalFieldKey,
      preventativeControls: { causesRecordLinkFieldId },
      mitigatingControls: { consequencesRecordLinkFieldId },
    },
    // Escalation factors configuration (optional)
    escalationFactors: escalationFactorsConfig,
    escalationFactorBarriers: escalationFactorBarriersConfig,
  } = configuration;

  const draftWorkflowStepLabel = useMemo(() => {
    return controlFormWorkflowSteps?.find((step) => step.draft)?.label ?? 'Draft';
  }, [controlFormWorkflowSteps]);

  // mue / risk scenario record
  const {
    data: mueRecord,
    isFetching: isMueFetching,
    isError: isMueError,
  } = useGetSimpleRecordQuery(
    { id: Number(recordId), params: {} },
    { skip: !recordId || Boolean(recordId && !isRecordIdValid) }
  );

  // causes
  const causesRecordIds = (mueRecord?.fields?.[causesReverseRecordLinkFieldId] as Array<number>) || [];

  const { data: causesRecords, isFetching: isCausesFetching } = useGetSimpleRecordsQuery(
    {
      formId: causesFormId,
      filter: `id:in:${causesRecordIds?.join(',')}`,
      pageSize: 50,
    },
    {
      skip: isMueFetching || !mueRecord || !causesRecordIds || causesRecordIds.length === 0,
    }
  );

  // consequences
  const consequencesRecordIds = (mueRecord?.fields?.[consequencesReverseRecordLinkFieldId] as Array<number>) || [];

  const { data: consequencesRecords, isFetching: isConsequencesFetching } = useGetSimpleRecordsQuery(
    {
      formId: consequencesFormId,
      filter: `id:in:${consequencesRecordIds?.join(',')}`,
      pageSize: 50,
    },
    {
      skip: isMueFetching || !mueRecord || !consequencesRecordIds || consequencesRecordIds.length === 0,
    }
  );

  // preventative controls
  const preventativeControlsRecordIds = mueRecord?.fields?.[preventativeControlsRecordLinkFieldId] as Array<string>;

  const { data: preventativeControlsRecords, isFetching: isPreventativeControlsFetching } = useGetSimpleRecordsQuery(
    {
      formId: controlFormId,
      filter: `id:in:${preventativeControlsRecordIds?.join(',')}`,
      pageSize: 200,
    },
    {
      skip: isMueFetching || !mueRecord || !preventativeControlsRecordIds || preventativeControlsRecordIds.length === 0,
    }
  );

  // mitigating controls
  const mitigatingControlsRecordIds = mueRecord?.fields?.[mitigatingControlsRecordLinkFieldId] as Array<string>;

  const { data: mitigatingControlsRecords, isFetching: isMitigatingControlsFetching } = useGetSimpleRecordsQuery(
    {
      formId: controlFormId,
      filter: `id:in:${mitigatingControlsRecordIds?.join(',')}`,
      pageSize: 200,
    },
    {
      skip: isMueFetching || !mueRecord || !mitigatingControlsRecordIds || mitigatingControlsRecordIds.length === 0,
    }
  );

  // escalation factors (optional - only if configuration exists)
  // Use the reverse record link field from the CRA, just like causes and consequences
  const escalationFactorsRecordIds =
    escalationFactorsReverseRecordLinkFieldId && escalationFactorsConfig
      ? (mueRecord?.fields?.[escalationFactorsReverseRecordLinkFieldId] as Array<number>) || []
      : [];

  const { data: escalationFactorsRecords, isFetching: isEscalationFactorsFetching } = useGetSimpleRecordsQuery(
    {
      formId: escalationFactorsConfig?.formId ?? 0,
      filter: `id:in:${escalationFactorsRecordIds.join(',')}`,
      pageSize: 50,
    },
    {
      skip:
        !escalationFactorsConfig ||
        isMueFetching ||
        !mueRecord ||
        !escalationFactorsRecordIds ||
        !Array.isArray(escalationFactorsRecordIds) ||
        escalationFactorsRecordIds.length === 0,
    }
  );

  // escalation factor barriers (optional - only if configuration exists)
  // Collect all barrier IDs from the escalation factors' reverse link field
  const barrierRecordIds = useMemo(() => {
    if (
      !escalationFactorsRecords ||
      !escalationFactorsConfig?.barriersReverseRecordLinkFieldId ||
      !escalationFactorBarriersConfig
    ) {
      return [];
    }

    // Extract barrier IDs from each escalation factor's reverse link field
    const allBarrierIds: number[] = [];
    const barriersReverseRecordLinkFieldId = escalationFactorsConfig.barriersReverseRecordLinkFieldId ?? 0;
    escalationFactorsRecords.forEach((ef) => {
      const barrierIds = ef.fields?.[barriersReverseRecordLinkFieldId];
      if (Array.isArray(barrierIds)) {
        allBarrierIds.push(...barrierIds);
      }
    });

    return [...new Set(allBarrierIds)]; // Remove duplicates
  }, [escalationFactorsRecords, escalationFactorsConfig, escalationFactorBarriersConfig]);

  const { data: escalationFactorBarriersRecords, isFetching: isEscalationFactorBarriersFetching } =
    useGetSimpleRecordsQuery(
      {
        formId: escalationFactorBarriersConfig?.formId ?? 0,
        filter: `id:in:${barrierRecordIds.join(',')}`,
        pageSize: 200,
      },
      {
        skip:
          !escalationFactorBarriersConfig ||
          !barrierRecordIds ||
          barrierRecordIds.length === 0 ||
          isEscalationFactorsFetching,
      }
    );

  // construct the diagram data
  if (
    recordId &&
    !isMueFetching &&
    !isCausesFetching &&
    !isConsequencesFetching &&
    !isPreventativeControlsFetching &&
    !isMitigatingControlsFetching &&
    !isEscalationFactorsFetching &&
    !isEscalationFactorBarriersFetching &&
    !diagramDataRef.current &&
    mueRecord?.id
  ) {
    diagramDataRef.current = {
      mue: { id: crypto.randomUUID() },
      causes: [],
      consequences: [],
    };

    // mue
    diagramDataRef.current.mue = {
      ...diagramDataRef.current.mue,
      recordId: mueRecord.id,
      linkUrl: mueRecord.linkUrl,
      label: mueRecord?.fields?.[scenarioFieldId] as string,
    };

    // hazard
    diagramDataRef.current.hazard = {
      id: crypto.randomUUID(),
      label: mueRecord?.fields?.[hazardFieldId] as string,
    };

    // causes and preventative controls
    const linkedPreventativeControlRecords =
      preventativeControlsRecords
        ?.filter((record) => record.fields)
        .map((controlRecord) => {
          // filter the preventative control record IDs which are also linked to the mue record (present in the causes record link field)
          let controlCausesRecordIds = (controlRecord.fields![causesRecordLinkFieldId] as Array<string>) ?? [];

          controlCausesRecordIds = controlCausesRecordIds.filter((id) => causesRecordIds.includes(Number(id)));

          if (controlCausesRecordIds.length > 0) {
            return controlRecord;
          }
        })
        .filter((controlRecord): controlRecord is TransformedSimpleRecordDto => Boolean(controlRecord)) ?? [];

    diagramDataRef.current.causes =
      causesRecordIds
        ?.map((recordId) => {
          // The 'causesRecords' array does not come in the order based on the specified query id list, so we need to ensure the field order is respected
          const causeRecord = causesRecords?.find((record) => record.id === recordId);
          if (causeRecord && causeRecord.fields) {
            // the list defines the node order in the diagram
            const preventativeControlsIdList = causeRecord.fields[causesControlsRecordLinkFieldId] as Array<number>;

            const controls: Array<ControlDiagramNode> =
              preventativeControlsIdList
                ?.map((controlId) => {
                  const controlRecord = linkedPreventativeControlRecords.find((record) => record.id === controlId);
                  if (controlRecord) {
                    const isGlobal = Boolean(controlRecord.fields![globalFieldKey]);
                    const isDraft = controlRecord.status === draftWorkflowStepLabel;

                    const criticalOrNonCritical = resolveCriticalControlType(
                      controlRecord.fields![criticalOrNonCriticalFieldId]
                    );

                    const effectiveOrNotEffective = resolveEffectiveControlType(
                      (controlRecord as Record<string, unknown>)[effectiveOrNotEffectiveField]
                    );

                    return {
                      id: crypto.randomUUID(),
                      recordId: controlRecord!.id,
                      parentRecordId: causeRecord.id,
                      linkUrl: controlRecord!.linkUrl,
                      label: controlRecord!.fields?.[controlCaptionFieldId] as string,
                      controlType: controlRecord!.fields?.[controlTypeFieldId] as IdValueType,
                      controlEffectiveness: controlRecord!.fields?.[controlEffectivenessFieldId] as IdValueType,
                      global: isGlobal,
                      draft: isDraft,
                      criticalControlType: criticalOrNonCritical,
                      effectiveControlType: effectiveOrNotEffective,
                    } as ControlDiagramNode;
                  }
                })
                .filter((record): record is ControlDiagramNode => Boolean(record)) ?? [];

            return {
              id: crypto.randomUUID(),
              recordId: causeRecord.id,
              linkUrl: causeRecord.linkUrl,
              label: causeRecord.fields[causesCaptionFieldId] as string,
              controls,
            } as CauseDiagramNode;
          }
        })
        .filter((cause): cause is CauseDiagramNode => Boolean(cause)) ?? [];

    // consequences and mitigating controls
    const linkedMitigatingControlRecords =
      mitigatingControlsRecords
        ?.filter((record) => record.fields)
        .map((controlRecord) => {
          // filter the mitigating control record IDs which are also linked to the mue record (present in the consequences record link field)
          let controlConsequencesRecordIds =
            (controlRecord.fields![consequencesRecordLinkFieldId] as Array<string>) ?? [];

          controlConsequencesRecordIds = controlConsequencesRecordIds.filter((id) =>
            consequencesRecordIds.includes(Number(id))
          );

          if (controlConsequencesRecordIds.length > 0) {
            return controlRecord;
          }
        })
        .filter((controlRecord): controlRecord is TransformedSimpleRecordDto => Boolean(controlRecord)) ?? [];

    diagramDataRef.current.consequences =
      consequencesRecordIds
        ?.map((recordId) => {
          // The 'consequencesRecords' array does not come in the order based on the specified query id list, so we need to ensure the field order is respected
          const consequenceRecord = consequencesRecords?.find((record) => record.id === recordId);
          if (consequenceRecord && consequenceRecord.fields) {
            // the list defines the node order in the diagram
            const mitigatingControlsIdList = consequenceRecord.fields[
              consequencesControlsRecordLinkFieldId
            ] as Array<number>;

            const controls: Array<ControlDiagramNode> =
              mitigatingControlsIdList
                ?.map((controlId) => {
                  const controlRecord = linkedMitigatingControlRecords.find((record) => record.id === controlId);
                  if (controlRecord) {
                    const controlConsequencesRecordIds =
                      (controlRecord.fields![consequencesRecordLinkFieldId] as Array<string>) ?? [];

                    if (controlConsequencesRecordIds.includes(String(consequenceRecord.id))) {
                      const isGlobal = Boolean(controlRecord.fields![globalFieldKey]);
                      const isDraft = controlRecord.status === draftWorkflowStepLabel;

                      const criticalOrNonCritical = resolveCriticalControlType(
                        controlRecord.fields![criticalOrNonCriticalFieldId]
                      );

                      const effectiveOrNotEffective = resolveEffectiveControlType(
                        (controlRecord as Record<string, unknown>)[effectiveOrNotEffectiveField]
                      );

                      return {
                        id: crypto.randomUUID(),
                        recordId: controlRecord!.id,
                        parentRecordId: consequenceRecord.id,
                        linkUrl: controlRecord!.linkUrl,
                        label: controlRecord!.fields?.[controlCaptionFieldId] as string,
                        controlType: controlRecord!.fields?.[controlTypeFieldId] as IdValueType,
                        controlEffectiveness: controlRecord!.fields?.[controlEffectivenessFieldId] as IdValueType,
                        global: isGlobal,
                        draft: isDraft,
                        criticalControlType: criticalOrNonCritical,
                        effectiveControlType: effectiveOrNotEffective,
                      } as ControlDiagramNode;
                    }
                  }
                })
                .filter((record): record is ControlDiagramNode => Boolean(record)) ?? [];

            return {
              id: crypto.randomUUID(),
              recordId: consequenceRecord.id,
              linkUrl: consequenceRecord.linkUrl,
              label: consequenceRecord.fields[consequencesCaptionFieldId] as string,
              controls,
            } as ConsequenceDiagramNode;
          }
        })
        .filter((consequence): consequence is ConsequenceDiagramNode => Boolean(consequence)) ?? [];

    // Process escalation factors and barriers as flat arrays (if data is available)
    if (
      escalationFactorsConfig &&
      escalationFactorBarriersConfig &&
      escalationFactorsRecords &&
      escalationFactorsRecords.length > 0 &&
      !isEscalationFactorsFetching &&
      !isEscalationFactorBarriersFetching
    ) {
      const allControls = [
        ...diagramDataRef.current.causes.flatMap((cause) => cause.controls),
        ...diagramDataRef.current.consequences.flatMap((consequence) => consequence.controls),
      ];

      const { escalationFactorRecordLinkFieldId, controlRecordLinkFieldId } = escalationFactorBarriersConfig;
      // Create flat array of barriers with their relationships
      const barriers: Array<EscalationFactorBarrierDiagramNode> =
        (escalationFactorBarriersRecords
          ?.map((barrierRecord) => {
            const efLinkField = barrierRecord.fields?.[escalationFactorRecordLinkFieldId];
            const efRecordId = Array.isArray(efLinkField) ? Number(efLinkField[0]) : Number(efLinkField);

            const controlLinkField = barrierRecord.fields?.[controlRecordLinkFieldId];
            const controlRecordId = Array.isArray(controlLinkField)
              ? Number(controlLinkField[0])
              : Number(controlLinkField);

            const control = allControls.find((control) => control.recordId === controlRecordId);

            if (!efRecordId || !controlRecordId || !control) {
              return;
            }

            return {
              id: crypto.randomUUID(),
              recordId: barrierRecord.id,
              linkUrl: barrierRecord.linkUrl,
              label: barrierRecord.fields?.[escalationFactorBarriersConfig.fieldId] as string,
              // escalationFactorNodeId will be linked when creating EFs below
              escalationFactorRecordId: efRecordId,
              controlRecordId: controlRecordId, // parentRecordId
              controlNodeId: control.id,
            };
          })
          .filter(Boolean) as Array<EscalationFactorBarrierDiagramNode>) ?? [];

      // Create flat array of EFs with their barrier relationship
      const escalationFactors: Array<EscalationFactorDiagramNode> = escalationFactorsRecords
        .map((efRecord) => {
          const controlLinkField = efRecord.fields?.[escalationFactorsConfig.controlRecordLinkFieldId];
          const controlRecordId = Array.isArray(controlLinkField)
            ? Number(controlLinkField[0]) // Link to the control record ID (assumes one control per EF)
            : Number(controlLinkField);

          const control = allControls.find((control) => control.recordId === controlRecordId);

          if (!efRecord.id || !controlRecordId || !control) {
            return;
          }

          // Find the barriers that links to this EF node
          const _barriers = barriers.filter((b) => b.escalationFactorRecordId === efRecord.id);

          const escalationFactorNodeId = crypto.randomUUID();
          // Link back the escalationFactorNodeId to each barrier
          _barriers.forEach((barrier) => {
            barrier.escalationFactorNodeId = escalationFactorNodeId;
          });

          return {
            id: escalationFactorNodeId,
            recordId: efRecord.id,
            linkUrl: efRecord.linkUrl,
            label: efRecord.fields?.[escalationFactorsConfig.fieldId] as string,
            barriers: _barriers, // Link to the barrier object
            controlRecordId: controlRecordId, // Link to the control record ID (assumes one control per EF)
            controlNodeId: control.id, // Link to the control node ID (assumes one control per EF)
          } as EscalationFactorDiagramNode;
        })
        .filter(Boolean) as Array<EscalationFactorDiagramNode>;

      diagramDataRef.current.escalationFactors = escalationFactors;
    }
  } else if (!recordId) {
    diagramDataRef.current = {
      mue: { id: crypto.randomUUID(), editMode: true },
      causes: [],
      consequences: [],
    };
  }

  const isFetching =
    isMueFetching ||
    isCausesFetching ||
    isConsequencesFetching ||
    isPreventativeControlsFetching ||
    isMitigatingControlsFetching ||
    isEscalationFactorsFetching ||
    isEscalationFactorBarriersFetching;

  const isError = !isRecordIdValid || isMueError;

  if (isError) {
    diagramDataRef.current = undefined;
  }

  return {
    isFetching,
    isError,
    diagramData: diagramDataRef.current,
  };
};

export default useRecord;

// util
export const resolveCriticalControlType = (fieldValue?: unknown) => {
  if (fieldValue && isObject(fieldValue) && 'value' in fieldValue) {
    if (fieldValue.value === criticalFilterValues.truthy) {
      return CriticalControlEnum.CRITICAL;
    } else if (fieldValue.value === criticalFilterValues.falsy) {
      return CriticalControlEnum.NON_CRITICAL;
    }
  }
  return undefined;
};

export const resolveEffectiveControlType = (fieldValue?: unknown) => {
  if (fieldValue) {
    if (fieldValue === effectiveFilterValues.truthy) {
      return EffectiveControlEnum.EFFECTIVE;
    } else if (fieldValue === effectiveFilterValues.falsy) {
      return EffectiveControlEnum.NON_EFFECTIVE;
    }
  }
  return undefined;
};
